import type { GraphState, Node } from "./types";

/**
 * Lightweight, deterministic force-directed layout.
 *
 *   F = repulsion (Coulomb) + attraction (Hooke along edges) + centring
 *   v += F * dt;  v *= damping;  pos += v * dt;
 *
 * Scaled to canonical [-1, 1] coordinate space — the renderer maps to canvas
 * pixels separately. Step is called every animation frame. The simulation is
 * critically damped so it settles within a few hundred ms when nodes are
 * mutated by the event log.
 */
export type LayoutParams = {
  repulsion: number;   // node-node Coulomb constant
  spring: number;      // edge spring constant
  springLength: number;// rest length for edges
  centring: number;    // pull toward centrePair midpoint
  damping: number;     // velocity * (1 - damping) per step
  vMax: number;        // velocity cap
  centrePair?: [string, string];
};

export const defaultLayoutParams: LayoutParams = {
  repulsion: 0.0042,
  spring: 0.045,
  springLength: 0.34,
  centring: 0.012,
  damping: 0.18,
  vMax: 0.06,
};

export function step(graph: GraphState, dt: number, params: LayoutParams = defaultLayoutParams) {
  const nodes = Array.from(graph.nodes.values());

  // resolve centring origin
  let cx = 0;
  let cy = 0;
  if (params.centrePair) {
    const a = graph.nodes.get(params.centrePair[0]);
    const b = graph.nodes.get(params.centrePair[1]);
    if (a && b) {
      cx = (a.x + b.x) / 2;
      cy = (a.y + b.y) / 2;
    }
  }

  // O(n^2) Coulomb — fine for ~30 nodes.
  for (let i = 0; i < nodes.length; i++) {
    const a = nodes[i];
    if (a.fixed) continue;
    let fx = 0, fy = 0;

    for (let j = 0; j < nodes.length; j++) {
      if (i === j) continue;
      const b = nodes[j];
      const dx = a.x - b.x;
      const dy = a.y - b.y;
      const d2 = dx * dx + dy * dy + 0.001;
      const f = params.repulsion / d2;
      fx += dx * f;
      fy += dy * f;
    }

    // centring force
    fx += (cx - a.x) * params.centring;
    fy += (cy - a.y) * params.centring;

    a.vx = (a.vx + fx * dt) * (1 - params.damping);
    a.vy = (a.vy + fy * dt) * (1 - params.damping);
  }

  // Hooke springs along every edge.
  for (const edge of graph.edges.values()) {
    const a = graph.nodes.get(edge.from);
    const b = graph.nodes.get(edge.to);
    if (!a || !b) continue;
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const d  = Math.sqrt(dx * dx + dy * dy) || 0.001;
    const stretch = d - params.springLength;
    const f = params.spring * stretch;
    const fx = (dx / d) * f;
    const fy = (dy / d) * f;
    if (!a.fixed) { a.vx += fx * dt; a.vy += fy * dt; }
    if (!b.fixed) { b.vx -= fx * dt; b.vy -= fy * dt; }
  }

  // Cap velocity, integrate position, write back.
  const cap = params.vMax;
  for (const a of nodes) {
    if (a.fixed) {
      a.x = a.fixed.x;
      a.y = a.fixed.y;
      a.vx = 0; a.vy = 0;
      continue;
    }
    const speed = Math.hypot(a.vx, a.vy);
    if (speed > cap) {
      a.vx *= cap / speed;
      a.vy *= cap / speed;
    }
    a.x += a.vx * dt;
    a.y += a.vy * dt;
    // Soft walls at the canonical [-1, 1] bounds. Without this, weakly
    // connected nodes drift past the projection range (sx/sy maps to the
    // canvas) and disappear off-screen — most visible on the smaller
    // FinalReveal network panel. Clamp + zero the outward velocity so they
    // settle at the edge instead of bouncing.
    if (a.x < -1) { a.x = -1; if (a.vx < 0) a.vx = 0; }
    if (a.x >  1) { a.x =  1; if (a.vx > 0) a.vx = 0; }
    if (a.y < -1) { a.y = -1; if (a.vy < 0) a.vy = 0; }
    if (a.y >  1) { a.y =  1; if (a.vy > 0) a.vy = 0; }
  }
}

/** Run the simulation forward by `iterations` synchronous steps to settle
 *  initial positions before the first paint. */
export function settle(graph: GraphState, iterations = 80, params: LayoutParams = defaultLayoutParams) {
  for (let i = 0; i < iterations; i++) step(graph, 1.0, params);
}

export const _internals = { defaultLayoutParams };
export type { Node };
